import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Popper, Paper, Zoom, Box } from '@mui/material';
import { styled } from '@mui/system';

const TooltipContent = styled(Paper)(({ theme }) => ({
  position: 'relative',
  padding: '8px 16px',
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(26, 26, 37, 0.98)'
    : 'rgba(0, 0, 0, 0.9)',
  color: theme.palette.mode === 'dark' ? '#E0E0E5' : '#fff',
  borderRadius: '8px',
  fontSize: '0.8125rem',
  maxWidth: '300px',
  wordWrap: 'break-word',
  fontWeight: 500,
  lineHeight: 1.5,
  letterSpacing: '0.01em',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 8px 24px rgba(0, 0, 0, 0.4)'
    : '0 8px 24px rgba(0, 0, 0, 0.2)',
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(138, 43, 226, 0.2)'
    : '1px solid rgba(138, 43, 226, 0.1)',
  backdropFilter: 'blur(8px)',
  transform: 'translateZ(0)',
  willChange: 'transform, opacity',
  WebkitFontSmoothing: 'antialiased',
  MozOsxFontSmoothing: 'grayscale',
  background: theme => `
    linear-gradient(135deg, 
      ${theme.palette.mode === 'dark' ? 'rgba(26, 26, 37, 0.98)' : 'rgba(0, 0, 0, 0.9)'} 0%,
      ${theme.palette.mode === 'dark' ? 'rgba(26, 26, 37, 0.95)' : 'rgba(0, 0, 0, 0.85)'} 100%
    )
  `,
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: theme => `
      radial-gradient(circle at center, ${theme.palette.primary.main}15 0%, transparent 50%),
      repeating-linear-gradient(45deg, ${theme.palette.primary.main}08 0px, ${theme.palette.primary.main}08 1px, transparent 1px, transparent 8px),
      repeating-linear-gradient(-45deg, ${theme.palette.primary.main}08 0px, ${theme.palette.primary.main}08 1px, transparent 1px, transparent 8px)
    `,
    backgroundSize: '100% 100%, 16px 16px, 16px 16px',
    opacity: 0.1,
    zIndex: -1,
    borderRadius: 'inherit',
  },
  '&[data-popper-placement*="top"]::after': {
    content: '""',
    position: 'absolute',
    bottom: -6,
    left: '50%',
    transform: 'translateX(-50%)',
    border: '6px solid transparent',
    borderTopColor: theme.palette.mode === 'dark' 
      ? 'rgba(26, 26, 37, 0.98)'
      : 'rgba(0, 0, 0, 0.9)',
  },
  '&[data-popper-placement*="bottom"]::after': {
    content: '""',
    position: 'absolute',
    top: -6,
    left: '50%',
    transform: 'translateX(-50%)',
    border: '6px solid transparent',
    borderBottomColor: theme.palette.mode === 'dark' 
      ? 'rgba(26, 26, 37, 0.98)'
      : 'rgba(0, 0, 0, 0.9)',
  },
  '&[data-popper-placement*="left"]::after': {
    content: '""',
    position: 'absolute',
    right: -6,
    top: '50%',
    transform: 'translateY(-50%)',
    border: '6px solid transparent',
    borderLeftColor: theme.palette.mode === 'dark' 
      ? 'rgba(26, 26, 37, 0.98)'
      : 'rgba(0, 0, 0, 0.9)',
  },
  '&[data-popper-placement*="right"]::after': {
    content: '""',
    position: 'absolute',
    left: -6,
    top: '50%',
    transform: 'translateY(-50%)',
    border: '6px solid transparent',
    borderRightColor: theme.palette.mode === 'dark' 
      ? 'rgba(26, 26, 37, 0.98)'
      : 'rgba(0, 0, 0, 0.9)',
  },
}));

const CustomTooltip = ({ children, title, placement = 'top', offset = 8 }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipPlacement, setTooltipPlacement] = useState(placement);
  
  const enterTimeout = useRef(null);
  const leaveTimeout = useRef(null);
  const arrowRef = useRef(null);
  const tooltipRef = useRef(null);

  const calculateTooltipPlacement = useCallback((target) => {
    const rect = target.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const isCanvasEditor = window.location.pathname === '/canvas-editor';
    
    const tooltipHeight = tooltipRef.current?.offsetHeight || 0;
    const tooltipWidth = tooltipRef.current?.offsetWidth || 0;
    
    const spaceAbove = rect.top;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceLeft = rect.left;
    const spaceRight = viewportWidth - rect.right;
    
    const padding = isCanvasEditor ? 16 : 8;
    
    let bestPlacement = placement;
    
    if (placement === 'top' && spaceAbove < (tooltipHeight + padding)) {
      bestPlacement = spaceBelow >= (tooltipHeight + padding) ? 'bottom' : 
                     spaceRight >= (tooltipWidth + padding) ? 'right' : 'left';
    } else if (placement === 'bottom' && spaceBelow < (tooltipHeight + padding)) {
      bestPlacement = spaceAbove >= (tooltipHeight + padding) ? 'top' : 
                     spaceRight >= (tooltipWidth + padding) ? 'right' : 'left';
    } else if (placement === 'left' && spaceLeft < (tooltipWidth + padding)) {
      bestPlacement = spaceRight >= (tooltipWidth + padding) ? 'right' : 
                     spaceBelow >= (tooltipHeight + padding) ? 'bottom' : 'top';
    } else if (placement === 'right' && spaceRight < (tooltipWidth + padding)) {
      bestPlacement = spaceLeft >= (tooltipWidth + padding) ? 'left' : 
                     spaceBelow >= (tooltipHeight + padding) ? 'bottom' : 'top';
    }
    
    return bestPlacement;
  }, [placement]);

  const handleMouseEnter = useCallback((event) => {
    if (leaveTimeout.current) {
      clearTimeout(leaveTimeout.current);
      leaveTimeout.current = null;
    }

    const target = event.currentTarget;
    const newPlacement = calculateTooltipPlacement(target);
    setTooltipPlacement(newPlacement);

    enterTimeout.current = setTimeout(() => {
      setAnchorEl(target);
      setIsOpen(true);
    }, 100);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (enterTimeout.current) {
      clearTimeout(enterTimeout.current);
      enterTimeout.current = null;
    }

    leaveTimeout.current = setTimeout(() => {
      setIsOpen(false);
    }, 100);
  }, []);

  useEffect(() => {
    return () => {
      if (enterTimeout.current) clearTimeout(enterTimeout.current);
      if (leaveTimeout.current) clearTimeout(leaveTimeout.current);
    };
  }, []);

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{ display: 'inline-flex' }}
    >
      {children}
      <Popper
        open={isOpen}
        anchorEl={anchorEl}
        placement={tooltipPlacement}
        transition
        disablePortal
        ref={tooltipRef}
        style={{
          zIndex: 2000,
          pointerEvents: 'none'
        }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, offset],
            },
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              padding: 8,
              altAxis: true,
              tetherOffset: 100,
            },
          },
          {
            name: 'flip',
            options: {
              boundary: 'viewport',
              padding: 8,
              fallbackPlacements: ['top', 'bottom', 'right', 'left'],
            },
          },
          {
            name: 'arrow',
            options: {
              element: arrowRef.current,
              padding: window.location.pathname === '/canvas-editor' ? 12 : 8,
            },
          },
          {
            name: 'computeStyles',
            options: {
              gpuAcceleration: true,
              adaptive: true,
            },
          },
          {
            name: 'eventListeners',
            options: {
              scroll: true,
              resize: true,
            },
          }
        ]}
      >
        {({ TransitionProps, placement }) => (
          <Zoom {...TransitionProps} timeout={200}>
            <TooltipContent data-popper-placement={placement}>
              {title}
            </TooltipContent>
          </Zoom>
        )}
      </Popper>
    </Box>
  );
};

export default CustomTooltip;
